import { PhaserAudioManager } from '@/game/GameComponents/PhaserGame/Utils/PhaserAudioManager';
import { HOVER_CURSOR_PATH } from '@/game/GameComponents/Utils/GameSpriteAssetsMap';

export class ButtonController {
  constructor(private config: ButtonControllerConfig) {
    const { buttonBacking, buttonLabel } = config;
    const labelOrigin = { x: buttonLabel.x, y: buttonLabel.y };
    this.labelOrigin = labelOrigin;

    // Submit button initialization.
    buttonBacking.setInteractive({ cursor: `${HOVER_CURSOR_PATH}, auto` });
    buttonBacking.on('pointerover', () => {
      buttonBacking.setTexture(config.hoverTexture);
      buttonLabel.setPosition(labelOrigin.x, labelOrigin.y + 2);
      PhaserAudioManager.playSFX(
        buttonBacking.scene,
        config.hoverSFX ?? 'hover-click',
      );
    });

    buttonBacking.on('pointerdown', async () => {
      // Add a short input delay to prevent button spamming
      // and clickthrough.
      buttonBacking.scene.input.enabled = false;

      buttonBacking.scene.time.delayedCall(150, () => {
        if (this.isInteractive) {
          buttonBacking.setTexture(config.defaultTexture);
          buttonLabel.setPosition(labelOrigin.x, labelOrigin.y);
        }

        buttonBacking.scene.input.enabled = true;
      });
      buttonBacking.setTexture(config.hoverTexture);
      buttonLabel.setPosition(labelOrigin.x, labelOrigin.y);
      PhaserAudioManager.playSFX(
        buttonBacking.scene,
        config.clickSFX ?? 'submit-click',
      );
      this.onSubmitted?.();
    });
    buttonBacking.on('pointerout', () => {
      buttonBacking.setTexture(config.defaultTexture);
      buttonLabel.setPosition(labelOrigin.x, labelOrigin.y);
    });
    buttonBacking.on('pointerup', () => {
      buttonBacking.setTexture(config.defaultTexture);
      buttonLabel.setPosition(labelOrigin.x, labelOrigin.y + 2);
    });
  }

  isInteractive: boolean = true;

  onSubmitted?: () => void;
  labelOrigin: { x: number; y: number };

  setSubmissionCallback(submittedCallback: () => void) {
    this.onSubmitted = submittedCallback;
    return this;
  }

  makeInteractive(isInteractive: boolean) {
    const { buttonBacking, buttonLabel, defaultTexture, disabledTexture } =
      this.config;
    if (isInteractive) {
      buttonBacking.setInteractive({
        cursor: `${HOVER_CURSOR_PATH}, auto`,
      });
      buttonBacking.setTexture(defaultTexture);
      buttonLabel.setAlpha(1);
    } else {
      buttonBacking.disableInteractive();
      buttonLabel.setAlpha(0.5);
      buttonBacking.setTexture(disabledTexture);
      buttonLabel.setPosition(this.labelOrigin.x, this.labelOrigin.y);
    }
    this.isInteractive = isInteractive;
  }

  setText(text: string) {
    this.config.buttonLabel.setText(text);
  }

  showGuidance(show: boolean) {
    const { buttonGuidance } = this.config;
    if (!buttonGuidance || !buttonGuidance.scene) return;
    buttonGuidance.setVisible(show);
    if (show) {
      buttonGuidance.setAlpha(0);
      buttonGuidance.scene.tweens.killTweensOf(buttonGuidance);
      // Initialize guidance tween.
      buttonGuidance.scene.tweens.add({
        targets: buttonGuidance,
        alpha: 1,
        yoyo: true,
        repeat: -1,
        ease: 'Cubic.easeInOut',
      });
    }
  }
}

export interface ButtonControllerConfig {
  buttonBacking: Phaser.GameObjects.NineSlice | Phaser.GameObjects.Image;
  buttonLabel: Phaser.GameObjects.BitmapText;
  defaultTexture: string;
  hoverTexture: string;
  submitTexture: string;
  disabledTexture: string;
  buttonGuidance?: Phaser.GameObjects.Image;
  hoverSFX?: string;
  clickSFX?: string;
}
